<template>
  <header class="sticky top-0 z-30 bg-gray-50/95 backdrop-blur">
    <div class="nav-transition mx-auto max-w-[1600px] px-4 lg:px-8">
      <div class="flex flex-col gap-3 py-4 lg:grid lg:grid-cols-[auto_minmax(0,1fr)_auto] lg:items-center lg:gap-6">
        <div class="order-1 flex items-center justify-between gap-3 lg:block">
          <RouterLink to="/" class="text-lg font-semibold text-ink"><img src="@/assets/logo.png" alt="Invi8 Logo" style="height:40px"></RouterLink>  
          <div class="flex items-center gap-2 lg:hidden">
            <div v-if="isLoggedIn" class="relative flex items-center">
              <button
                ref="mobileProfileButtonRef"
                type="button"
                class="mobile-icon-btn mobile-profile-btn"
                @click.stop="profileMenuOpen = !profileMenuOpen"
              >
                <span class="flex h-8 w-8 items-center justify-center rounded-full bg-[#FFE5EC] text-brand">
                  <i class="ri-user-3-line text-base"></i>
                </span>
                <i class="ri-arrow-down-s-line text-base text-muted"></i>
              </button>
              <transition name="fade">
                <div
                  v-if="profileMenuOpen"
                  ref="mobileProfileDropdownRef"
                  class="absolute right-0 top-full mt-3 w-56 rounded-2xl border border-gray-100 bg-white p-3 shadow-[0_18px_40px_rgba(0,0,0,0.12)] z-40"
                >
                  <div class="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-muted">Account</div>
                  <RouterLink class="flex items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-ink transition hover:bg-gray-50" :to="accountHomeLink">
                    <span>{{ accountHomeLabel }}</span>
                    <i class="ri-dashboard-line text-sm text-muted"></i>
                  </RouterLink>
                  <RouterLink class="flex items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-ink transition hover:bg-gray-50" :to="accountSettingsLink">
                    <span>Settings</span>
                    <i class="ri-settings-3-line text-sm text-muted"></i>
                  </RouterLink>
                  <button
                    type="button"
                    class="mt-1 flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-ink transition hover:bg-gray-50"
                    @click="handleLogout"
                  >
                    <span>Logout</span>
                    <i class="ri-logout-box-r-line text-sm text-muted"></i>
                  </button>
                </div>
              </transition>
            </div>
            <RouterLink
              v-else
              class="mobile-icon-btn"
              to="/login"
              aria-label="Log in"
            >
              <i class="ri-user-line text-lg"></i>
            </RouterLink>
            <button v-if="!hideDashboardExtras" class="mobile-icon-btn" aria-label="Open menu" @click="ui.toggleSidenav">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        <div class="order-3 flex flex-col items-center justify-center gap-2 lg:order-2">
          <form
            v-if="showSearch"
            class="w-full lg:max-w-none"
            @submit.prevent="submitNavbarSearch"
          >
            <div class="relative grid w-full grid-cols-[minmax(0,1fr)_58px] overflow-visible rounded-[18px] border border-gray-200 bg-white shadow-sm lg:min-h-[60px] lg:grid-cols-[minmax(0,1fr)_64px] lg:rounded-[999px]">
              <label class="sr-only" for="navbar-location">Location</label>
              <div class="relative min-w-0">
                <i class="ri-map-pin-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-muted"></i>
                <input
                  ref="locationInputRef"
                  id="navbar-location"
                  :value="locationSearchQuery"
                  type="text"
                  readonly
                  placeholder="Location"
                  class="h-full w-full cursor-pointer rounded-l-[18px] border-0 bg-transparent px-11 py-3 pr-24 text-sm text-ink truncate transition focus:outline-none lg:rounded-l-[999px] lg:px-12 lg:pr-32"
                  @click="openLocationPicker"
                  @focus="openLocationPicker"
                >
                <span class="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#b58d72] lg:right-4 lg:text-xs lg:tracking-[0.24em]">
                  {{ searchType === 'vendors' ? 'Vendor' : 'Venue' }}
                </span>
              </div>

              <button
                type="submit"
                class="inline-flex items-center justify-center rounded-r-[18px] bg-brand text-white transition hover:bg-brandDark lg:m-1 lg:rounded-[999px]"
                aria-label="Search"
              >
                <i class="ri-search-line text-lg"></i>
              </button>
            </div>
          </form>
        </div>

        <div class="order-2 hidden items-center justify-end gap-3 self-end lg:flex lg:order-3 lg:self-center">
          <RouterLink v-if="!hideDashboardExtras" class="text-sm font-semibold text-ink" to="/vendor-signup">Become a Vendor</RouterLink> 
          <div v-if="isLoggedIn" class="relative flex items-center">
            <button
              ref="desktopProfileButtonRef"
              type="button"
              class="flex items-center gap-2 rounded-full border border-gray-200 bg-white px-2.5 py-1.5 text-sm font-semibold text-ink shadow-sm transition hover:border-gray-300"
              @click.stop="profileMenuOpen = !profileMenuOpen"
            >
              <span class="flex h-8 w-8 items-center justify-center rounded-full bg-[#FFE5EC] text-brand">
                <i class="ri-user-3-line text-base"></i>
              </span>
              <span class="hidden xl:inline">{{ displayName }}</span>
              <i class="ri-arrow-down-s-line text-base text-muted"></i>
            </button>
            <transition name="fade">
              <div
                v-if="profileMenuOpen"
                ref="desktopProfileDropdownRef"
                class="absolute right-0 top-full mt-3 w-56 rounded-2xl border border-gray-100 bg-white p-3 shadow-[0_18px_40px_rgba(0,0,0,0.12)] z-40"
              >
                <div class="px-2 pb-2 text-xs font-semibold uppercase tracking-wide text-muted">Account</div>
                <RouterLink class="flex items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-ink transition hover:bg-gray-50" :to="accountHomeLink">
                  <span>{{ accountHomeLabel }}</span>
                  <i class="ri-dashboard-line text-sm text-muted"></i>
                </RouterLink>
                <RouterLink class="flex items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-ink transition hover:bg-gray-50" :to="accountSettingsLink">
                  <span>Settings</span>
                  <i class="ri-settings-3-line text-sm text-muted"></i>
                </RouterLink>
                <button
                  type="button"
                  class="mt-1 flex w-full items-center justify-between rounded-xl px-3 py-2 text-sm font-semibold text-ink transition hover:bg-gray-50"
                  @click="handleLogout"
                >
                  <span>Logout</span>
                  <i class="ri-logout-box-r-line text-sm text-muted"></i>
                </button>
              </div>
            </transition>
          </div>
          <RouterLink
            v-else
            class="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-ink shadow-sm transition hover:border-gray-300"
            to="/login"
            aria-label="Log in"
          >
            <i class="ri-user-line text-lg"></i>
          </RouterLink>
          <div v-if="!hideDashboardExtras" class="relative hidden lg:block">
            <button ref="menuButtonRef" class="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-ink shadow-sm transition hover:border-gray-300" aria-label="Open menu" @click="desktopMenuOpen = !desktopMenuOpen">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <transition name="fade">
              <div
                v-if="desktopMenuOpen"
                ref="dropdownRef"
                class="absolute right-0 top-full mt-3 w-72 rounded-2xl border border-gray-100 bg-white p-4 shadow-[0_18px_40px_rgba(0,0,0,0.12)] z-40"
              >
                <div class="mb-2 flex items-center gap-2 text-sm font-semibold text-ink">
                  <span class="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-lg">?</span>
                  <span>Navigate</span>
                </div>
                <div class="grid gap-2 text-sm text-ink">
                  <RouterLink
                    v-for="link in navLinks"
                    :key="link.to"
                    class="flex items-center justify-between rounded-xl px-3 py-2 transition hover:bg-gray-50"
                    :to="link.to"
                  >
                    <span class="font-semibold">{{ link.label }}</span>
                    <span class="text-xs text-muted">→</span>
                  </RouterLink>
                </div>
                <div class="mt-3 flex gap-2">
                  <RouterLink
                    v-if="!isLoggedIn"
                    class="flex-1 rounded-full border border-gray-200 px-3 py-2 text-center text-xs font-semibold text-ink transition hover:border-brand hover:text-brand"
                    to="/login"
                  >
                    Log in
                  </RouterLink>
                  <RouterLink
                    v-if="!isLoggedIn"
                    class="flex-1 rounded-full bg-brand px-3 py-2 text-center text-xs font-semibold text-white transition hover:bg-brandDark"
                    to="/register"
                  >
                    Register
                  </RouterLink>
                  <RouterLink
                    v-if="isLoggedIn"
                    class="flex-1 rounded-full border border-gray-200 px-3 py-2 text-center text-xs font-semibold text-ink transition hover:border-brand hover:text-brand"
                    :to="accountSettingsLink"
                  >
                    Settings
                  </RouterLink>
                  <button
                    v-if="isLoggedIn"
                    type="button"
                    class="flex-1 rounded-full bg-brand px-3 py-2 text-center text-xs font-semibold text-white transition hover:bg-brandDark"
                    @click="handleLogout"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>
  </header>
  <Teleport to="body">
    <transition name="fade">
      <div
        v-if="locationSearchOpen"
        class="fixed inset-0 z-[120] overflow-y-auto bg-[#1f1a16]/55 backdrop-blur-sm"
        @click.self="closeLocationPicker"
      >
        <div class="flex min-h-screen items-start justify-center px-4 py-6 sm:items-center sm:p-6">
          <div class="w-full max-w-4xl overflow-hidden rounded-[28px] border border-[#eadfd4] bg-white shadow-[0_24px_80px_rgba(26,20,15,0.22)]">
            <div class="flex items-start justify-between gap-4 border-b border-[#f1e5da] px-5 py-4 sm:px-6">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.28em] text-[#b58d72]">Search</p>
                <h3 class="mt-1 text-lg font-semibold text-ink">Choose location and category</h3>
              </div>
              <button
                type="button"
                class="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-ink shadow-sm transition hover:border-brand hover:text-brand"
                aria-label="Close search"
                @click="closeLocationPicker"
              >
                <i class="ri-close-line text-lg"></i>
              </button>
            </div>

            <div class="max-h-[calc(100vh-3rem)] overflow-y-auto">
              <div class="grid gap-6 p-5 sm:p-6 lg:grid-cols-[minmax(0,1.2fr)_320px]">
                <div class="min-w-0">
                  <div class="relative">
                    <i class="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-lg text-muted"></i>
                    <input
                      ref="modalSearchInputRef"
                      v-model.trim="locationSearchQuery"
                      type="search"
                      placeholder="Search area, city, or address"
                      class="h-13 w-full rounded-2xl border border-[#eadfd4] bg-[#fffaf6] px-11 pr-4 text-sm text-ink outline-none transition focus:border-brand"
                      @input="searchNavbarLocation"
                    >
                  </div>

                  <div class="mt-4 flex flex-wrap gap-2">
                    <button
                      type="button"
                      class="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition"
                      :class="searchType === 'venues' ? 'border-brand bg-[#fff1e8] text-brand' : 'border-gray-200 bg-white text-ink hover:border-brand'"
                      @click="searchType = 'venues'"
                    >
                      <i class="ri-building-line text-base"></i>
                      Venue
                    </button>
                    <button
                      type="button"
                      class="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition"
                      :class="searchType === 'vendors' ? 'border-brand bg-[#fff1e8] text-brand' : 'border-gray-200 bg-white text-ink hover:border-brand'"
                      @click="searchType = 'vendors'"
                    >
                      <i class="ri-store-2-line text-base"></i>
                      Vendor
                    </button>
                  </div>

                  <button
                    type="button"
                    class="mt-4 flex w-full items-center gap-3 rounded-2xl border border-[#eadfd4] px-4 py-3 text-left transition hover:border-brand hover:bg-[#fbf1e6]"
                    :disabled="locationCurrentLoading"
                    @click="useNavbarCurrentLocation"
                  >
                    <span class="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#fff2ea] text-brand">
                      <i class="ri-navigation-line text-lg"></i>
                    </span>
                    <span class="min-w-0 flex-1">
                      <span class="block text-sm font-semibold text-ink">{{ locationCurrentLoading ? 'Detecting...' : 'Use current location' }}</span>
                      <span class="mt-1 block text-xs text-muted">Auto-fill from your device location</span>
                    </span>
                  </button>

                  <div class="mt-4 max-h-[320px] space-y-2 overflow-y-auto pr-1">
                    <button
                      v-for="(item, index) in locationSearchResults"
                      :key="`${item.placeId || item.label}-${index}`"
                      type="button"
                      class="flex w-full items-start gap-3 rounded-2xl border border-transparent px-4 py-3 text-left transition hover:border-[#f0dfcf] hover:bg-[#fcf7f2]"
                      @click="selectNavbarLocation(item)"
                    >
                      <span class="flex h-10 w-10 items-center justify-center rounded-2xl bg-[#f1f6ff] text-[#2d62b3]">
                        <i class="ri-map-pin-2-line text-lg"></i>
                      </span>
                      <span class="min-w-0 flex-1">
                        <span class="block text-sm font-semibold text-ink">{{ item.label }}</span>
                        <span class="mt-1 block text-xs text-muted">{{ item.meta }}</span>
                      </span>
                    </button>

                    <p v-if="locationSearchLoading" class="rounded-2xl border border-dashed border-[#eadfd4] px-4 py-4 text-sm text-muted">
                      Searching locations...
                    </p>
                    <p v-else-if="locationPickerError" class="rounded-2xl border border-dashed border-red-200 bg-red-50 px-4 py-4 text-sm text-red-600">
                      {{ locationPickerError }}
                    </p>
                    <p v-else-if="!locationSearchResults.length" class="rounded-2xl border border-dashed border-[#eadfd4] px-4 py-4 text-sm text-muted">
                      Enter at least 3 characters to search for a location.
                    </p>
                  </div>
                </div>

                <div class="space-y-4">
                  <div class="rounded-[24px] border border-[#eadfd4] bg-[#fffaf6] p-5">
                    <p class="text-xs font-semibold uppercase tracking-[0.26em] text-[#b58d72]">Map marker</p>
                    <div class="mt-4 overflow-hidden rounded-[22px] border border-[#e9d7c8] bg-white">
                      <div ref="modalMapRef" class="h-[220px] w-full"></div>
                      <div class="border-t border-[#f2e5d8] px-4 py-3 text-center">
                        <p class="text-sm font-semibold text-ink">{{ locationSearchQuery || 'Pick a location to continue' }}</p>
                        <p class="mt-2 text-xs leading-5 text-muted">
                          {{ searchType === 'vendors' ? 'Vendors' : 'Venues' }} near the selected place will open in the listing view.
                        </p>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    class="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark"
                    @click="submitNavbarSearch"
                  >
                    <i class="ri-search-line text-lg"></i>
                    Search {{ searchType === 'vendors' ? 'vendors' : 'venues' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import markerIcon from 'leaflet/dist/images/marker-icon.png'
import markerShadow from 'leaflet/dist/images/marker-shadow.png'
import { useUiStore } from '@/stores/ui'
import { useSearchStore } from '@/stores/search'
import { clearAuth, getAuthRole, getAuthUser, isAuthenticated } from '@/services/auth'

const GOOGLE_API_KEY = 'AIzaSyDOAEexenU_AutS0ULOGzNa3HQZHpikt4A'
const NAVBAR_SEARCH_STORAGE_KEY = 'invi8_frontend_navbar_search'

const ui = useUiStore()
const searchStore = useSearchStore()
const route = useRoute()
const router = useRouter()

L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  shadowUrl: markerShadow
})

const desktopMenuOpen = ref(false)
const dropdownRef = ref(null)
const menuButtonRef = ref(null)
const profileMenuOpen = ref(false)
const mobileProfileDropdownRef = ref(null)
const mobileProfileButtonRef = ref(null)
const desktopProfileDropdownRef = ref(null)
const desktopProfileButtonRef = ref(null)
const searchLocation = ref(String(route.query.location || ''))
const locationSearchQuery = ref(String(route.query.location || ''))
const locationSearchResults = ref([])
const locationSearchLoading = ref(false)
const locationCurrentLoading = ref(false)
const locationPickerError = ref('')
const locationSearchOpen = ref(false)
const locationInputRef = ref(null)
const modalSearchInputRef = ref(null)
const modalMapRef = ref(null)
const searchLat = ref(String(route.query.lat || ''))
const searchLng = ref(String(route.query.lng || ''))
const searchType = ref('venues')
const googleReady = ref(false)
const googleScriptLoading = ref(false)
const autocompleteService = ref(null)
const placesService = ref(null)
const geocoder = ref(null)
const modalMap = ref(null)
const modalMapMarker = ref(null)

const hasStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'

const getStoredNavbarSearch = () => {
  if (!hasStorage()) return null
  const raw = localStorage.getItem(NAVBAR_SEARCH_STORAGE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

const persistNavbarSearch = () => {
  if (!hasStorage()) return
  localStorage.setItem(
    NAVBAR_SEARCH_STORAGE_KEY,
    JSON.stringify({
      type: searchType.value || 'venues',
      location: searchLocation.value || locationSearchQuery.value || '',
      lat: searchLat.value || '',
      lng: searchLng.value || ''
    })
  )
}

const parseCoordinate = (value) => {
  const parsed = Number(String(value ?? '').trim())
  return Number.isFinite(parsed) ? parsed : null
}

const syncSearchStore = ({ text, lat, lng }) => {
  searchStore.setLocation({
    text: text || '',
    lat: lat === '' || lat === null || lat === undefined ? null : Number(lat),
    lng: lng === '' || lng === null || lng === undefined ? null : Number(lng)
  })
}

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/vendors', label: 'Vendors' },
  { to: '/venues', label: 'Venues' }, 
  { to: '/pricing', label: 'Pricing' },
  { to: '/faqs', label: 'FAQs' },
  { to: '/categories', label: 'Categories' },
  { to: '/use-cases', label: 'Use Cases' },
  { to: '/blog', label: 'Blog' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
  { to: '/privacy-policy', label: 'Privacy' },
  { to: '/terms', label: 'Terms' }
]
 
const isVendorDetail = computed(() => (router.currentRoute.value.path || '').startsWith('/vendor/'))
const currentPath = computed(() => router.currentRoute.value.path || '')
const currentName = computed(() => router.currentRoute.value.name || '')
const isDashboardRoute = computed(() => currentPath.value.toLowerCase().startsWith('/dashboard'))

const hiddenPaths = ['/vendor-signup', '/vendor-onboard', '/login', '/register', '/forgot-password', '/dashboard', '/404', '/not-found']
const hiddenNames = ['VendorSignup', 'VendorOnboard', 'Login', 'Register', 'ForgotPassword', 'Dashboard', 'DashboardVenues', 'DashboardPackages', 'DashboardSettings', 'NotFound']

const hideNavUi = computed(() => {
  const path = currentPath.value.toLowerCase()
  if (isVendorDetail.value) return true
  if (hiddenPaths.some((p) => path.startsWith(p))) return true
  const name = (currentName.value || '').toString().toLowerCase()
  return hiddenNames.map((n) => n.toLowerCase()).includes(name)
})

const showSearch = computed(() => !hideNavUi.value)
const hideDashboardExtras = computed(() => isLoggedIn.value && isDashboardRoute.value)
const navCollapsed = ref(false)
const forceExpanded = ref(false)
const forceExpandedAt = ref(0)

const handleClickOutside = (event) => {
  const target = event.target
  if (desktopMenuOpen.value) {
    if (dropdownRef.value && dropdownRef.value.contains(target)) return
    if (menuButtonRef.value && menuButtonRef.value.contains(target)) return
    desktopMenuOpen.value = false
  }
  if (profileMenuOpen.value) {
    if (mobileProfileDropdownRef.value && mobileProfileDropdownRef.value.contains(target)) return
    if (mobileProfileButtonRef.value && mobileProfileButtonRef.value.contains(target)) return
    if (desktopProfileDropdownRef.value && desktopProfileDropdownRef.value.contains(target)) return
    if (desktopProfileButtonRef.value && desktopProfileButtonRef.value.contains(target)) return
    profileMenuOpen.value = false
  }
}

const handleEsc = (event) => {
  if (event.key === 'Escape') {
    closeLocationPicker()
    desktopMenuOpen.value = false
    profileMenuOpen.value = false
  }
}

const collapseThreshold = 80
const handleScroll = () => {
  const y = window.scrollY || document.documentElement.scrollTop
  const nearTop = y < 12

  if (nearTop) {
    navCollapsed.value = false
    forceExpanded.value = false
    return
  }

  if (forceExpanded.value) {
    const moved = Math.abs(y - forceExpandedAt.value) > 50
    if (moved && y > forceExpandedAt.value) {
      forceExpanded.value = false
    } else {
      navCollapsed.value = false
      return
    }
  }

  navCollapsed.value = y > collapseThreshold
}

const getSearchTypeFromRoute = () => {
  const queryType = String(route.query.type || '').toLowerCase()
  if (queryType === 'vendors' || queryType === 'vendor') return 'vendors'
  if (queryType === 'venues' || queryType === 'venue') return 'venues'

  const storedType = String(getStoredNavbarSearch()?.type || '').toLowerCase()
  if (storedType === 'vendors' || storedType === 'vendor') return 'vendors'
  if (storedType === 'venues' || storedType === 'venue') return 'venues'

  const path = currentPath.value.toLowerCase()
  if (path.startsWith('/vendors')) return 'vendors'
  if (path.startsWith('/venues')) return 'venues'
  return 'venues'
}

const initGooglePlacesServices = () => {
  if (typeof window === 'undefined' || !window.google?.maps?.places) return false
  googleReady.value = true
  autocompleteService.value = autocompleteService.value || new window.google.maps.places.AutocompleteService()
  placesService.value = placesService.value || new window.google.maps.places.PlacesService(document.createElement('div'))
  geocoder.value = geocoder.value || new window.google.maps.Geocoder()
  googleScriptLoading.value = false
  return true
}

const ensureGooglePlaces = () => {
  if (initGooglePlacesServices()) return Promise.resolve(true)
  if (!GOOGLE_API_KEY) return Promise.resolve(false)

  return new Promise((resolve) => {
    const existingScript = document.querySelector('script[data-google-maps="places"], script[src*="maps.googleapis.com/maps/api/js"]')
    if (existingScript) {
      if (googleScriptLoading.value) {
        existingScript.addEventListener('load', () => resolve(initGooglePlacesServices()), { once: true })
        existingScript.addEventListener('error', () => resolve(false), { once: true })
        return
      }
      resolve(initGooglePlacesServices())
      return
    }

    googleScriptLoading.value = true
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`
    script.async = true
    script.defer = true
    script.dataset.googleMaps = 'places'
    script.onload = () => resolve(initGooglePlacesServices())
    script.onerror = () => {
      googleScriptLoading.value = false
      resolve(false)
    }
    document.head.appendChild(script)
  })
}

const openLocationPicker = () => {
  locationSearchOpen.value = true
  locationPickerError.value = ''
  nextTick(() => {
    modalSearchInputRef.value?.focus?.()
    ensureModalMap()
    updateModalMapPosition()
  })
  ensureGooglePlaces().then((loaded) => {
    if (!loaded && !locationSearchQuery.value.trim()) {
      locationPickerError.value = 'Google location search is unavailable right now.'
    }
  })
}

const closeLocationPicker = () => {
  locationSearchOpen.value = false
}

const ensureModalMap = () => {
  if (modalMap.value || !modalMapRef.value) return
  modalMap.value = L.map(modalMapRef.value, {
    zoomControl: true,
    attributionControl: true
  }).setView([20.5937, 78.9629], 5)

  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap contributors'
  }).addTo(modalMap.value)
}

const updateModalMapPosition = () => {
  if (!modalMap.value) return

  const lat = parseCoordinate(searchLat.value)
  const lng = parseCoordinate(searchLng.value)
  const hasCoords = lat !== null && lng !== null

  if (!hasCoords) {
    if (modalMapMarker.value) {
      modalMap.value.removeLayer(modalMapMarker.value)
      modalMapMarker.value = null
    }
    modalMap.value.setView([20.5937, 78.9629], 5)
    modalMap.value.invalidateSize()
    return
  }

  if (!modalMapMarker.value) {
    modalMapMarker.value = L.marker([lat, lng]).addTo(modalMap.value)
  } else {
    modalMapMarker.value.setLatLng([lat, lng])
  }

  modalMapMarker.value.bindPopup(locationSearchQuery.value || searchLocation.value || 'Selected location')
  modalMap.value.setView([lat, lng], 14)
  modalMap.value.invalidateSize()
}

const searchNavbarLocation = async () => {
  const query = locationSearchQuery.value.trim()
  searchLocation.value = query
  searchLat.value = ''
  searchLng.value = ''
  locationSearchOpen.value = true

  if (query.length < 3) {
    locationSearchResults.value = []
    locationPickerError.value = 'Enter at least 3 characters to search.'
    return
  }

  locationPickerError.value = ''
  locationSearchLoading.value = true
  try {
    const loaded = await ensureGooglePlaces()
    if (!loaded || !autocompleteService.value) {
      throw new Error('google-places-unavailable')
    }

    locationSearchResults.value = await new Promise((resolve, reject) => {
      autocompleteService.value.getPlacePredictions(
        {
          input: query
        },
        (predictions, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !Array.isArray(predictions)) {
            if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
              resolve([])
              return
            }
            reject(new Error(status || 'places-search-failed'))
            return
          }

          resolve(
            predictions.slice(0, 6).map((item) => ({
              placeId: item.place_id || '',
              label: item.structured_formatting?.main_text || item.description || query,
              meta: item.structured_formatting?.secondary_text || item.description || '',
              latitude: '',
              longitude: ''
            }))
          )
        }
      )
    })

    if (!locationSearchResults.value.length) {
      locationPickerError.value = 'No matching locations found.'
    }
  } catch {
    locationSearchResults.value = []
    locationPickerError.value = 'Unable to search locations right now.'
  } finally {
    locationSearchLoading.value = false
  }
}

const resolveNavbarPlaceDetails = (placeId) => {
  if (!placeId || !placesService.value || typeof window === 'undefined' || !window.google?.maps?.places) {
    return Promise.resolve(null)
  }

  return new Promise((resolve) => {
    placesService.value.getDetails(
      {
        placeId,
        fields: ['formatted_address', 'geometry.location', 'name']
      },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          resolve(null)
          return
        }

        resolve({
          label: place.formatted_address || place.name || '',
          latitude: place.geometry?.location?.lat?.() ?? '',
          longitude: place.geometry?.location?.lng?.() ?? ''
        })
      }
    )
  })
}

const selectNavbarLocation = async (item) => {
  if (!item?.label) return
  const placeDetails = item.placeId ? await resolveNavbarPlaceDetails(item.placeId) : null
  const resolvedLabel = placeDetails?.label || item.label
  searchLocation.value = resolvedLabel
  locationSearchQuery.value = resolvedLabel
  searchLat.value = String(placeDetails?.latitude || item.latitude || '')
  searchLng.value = String(placeDetails?.longitude || item.longitude || '')
  locationPickerError.value = ''
  syncSearchStore({
    text: resolvedLabel,
    lat: searchLat.value,
    lng: searchLng.value
  })
  persistNavbarSearch()
}

const reverseGeocodeNavbarLocation = async (latitude, longitude) => {
  const loaded = await ensureGooglePlaces()
  if (!loaded || !geocoder.value) {
    throw new Error('google-geocoder-unavailable')
  }

  const result = await geocoder.value.geocode({
    location: {
      lat: Number(latitude),
      lng: Number(longitude)
    }
  })

  return String(result?.results?.[0]?.formatted_address || 'Current location').trim()
}

const useNavbarCurrentLocation = async () => {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    locationPickerError.value = 'Geolocation is not supported in this browser.'
    return
  }

  locationPickerError.value = ''
  locationCurrentLoading.value = true
  try {
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000
      })
    })
    const latitude = position.coords.latitude
    const longitude = position.coords.longitude
    const label = await reverseGeocodeNavbarLocation(latitude, longitude)
    searchLocation.value = label
    locationSearchQuery.value = label
    searchLat.value = String(latitude)
    searchLng.value = String(longitude)
    syncSearchStore({
      text: label,
      lat: latitude,
      lng: longitude
    })
    persistNavbarSearch()
  } catch {
    locationPickerError.value = 'Unable to detect your current location.'
  } finally {
    locationCurrentLoading.value = false
  }
}

const submitNavbarSearch = () => {
  searchLocation.value = locationSearchQuery.value || searchLocation.value
  syncSearchStore({
    text: searchLocation.value,
    lat: searchLat.value,
    lng: searchLng.value
  })
  persistNavbarSearch()
  closeLocationPicker()
  router.push({
    path: searchType.value === 'vendors' ? '/vendors' : '/venues',
    query: {
      type: searchType.value,
      location: searchLocation.value || undefined,
      lat: searchLat.value || undefined,
      lng: searchLng.value || undefined
    }
  })
}

const bootstrapDefaultLocation = async () => {
  const hasSelectedLocation =
    Boolean(route.query.location) ||
    Boolean(route.query.lat || route.query.lng) ||
    Boolean(getStoredNavbarSearch()?.location) ||
    Boolean(getStoredNavbarSearch()?.lat || getStoredNavbarSearch()?.lng) ||
    Boolean(searchStore.locationText) ||
    Boolean(searchStore.lat || searchStore.lng)

  if (hasSelectedLocation) return
  await useNavbarCurrentLocation()
}

const isLoggedIn = ref(isAuthenticated())
const userProfile = ref(getAuthUser())
const authRole = ref(getAuthRole() || '')

const refreshAuthState = () => {
  isLoggedIn.value = isAuthenticated()
  userProfile.value = getAuthUser()
  authRole.value = getAuthRole() || ''
}

const accountHomeLink = computed(() => (authRole.value === 'vendor' ? '/dashboard' : '/host'))
const accountHomeLabel = computed(() => (authRole.value === 'vendor' ? 'Dashboard' : 'My Panel'))
const accountSettingsLink = computed(() => (authRole.value === 'vendor' ? '/dashboard/settings' : '/settings'))

const displayName = computed(() => {
  const user = userProfile.value || {}
  return (
    user.name ||
    user.full_name ||
    user.fullName ||
    user.username ||
    user.email ||
    'Account'
  )
})

const handleLogout = () => {
  clearAuth()
  refreshAuthState()
  router.push('/login')
}

watch(
  () => route.fullPath,
  () => {
    const storedSearch = getStoredNavbarSearch() || {}
    searchType.value = getSearchTypeFromRoute()
    searchLocation.value = String(route.query.location || storedSearch.location || '')
    locationSearchQuery.value = String(route.query.location || storedSearch.location || '')
    searchLat.value = String(route.query.lat || storedSearch.lat || '')
    searchLng.value = String(route.query.lng || storedSearch.lng || '')
    syncSearchStore({
      text: searchLocation.value,
      lat: searchLat.value,
      lng: searchLng.value
    })
    refreshAuthState()
  },
  { immediate: true }
)

watch(
  () => [locationSearchOpen.value, searchLat.value, searchLng.value, locationSearchQuery.value],
  () => {
    if (!locationSearchOpen.value) return
    nextTick(() => {
      ensureModalMap()
      updateModalMapPosition()
    })
  }
)

const removeAfterEach = router.afterEach(() => {
  desktopMenuOpen.value = false
  profileMenuOpen.value = false
})

onMounted(() => {
  window.addEventListener('click', handleClickOutside)
  window.addEventListener('keydown', handleEsc)
  window.addEventListener('scroll', handleScroll, { passive: true })
  handleScroll()
  refreshAuthState()
  bootstrapDefaultLocation()
})

onUnmounted(() => {
  window.removeEventListener('click', handleClickOutside)
  window.removeEventListener('keydown', handleEsc)
  window.removeEventListener('scroll', handleScroll)
  if (modalMap.value) {
    modalMap.value.remove()
    modalMap.value = null
    modalMapMarker.value = null
  }
  if (typeof removeAfterEach === 'function') removeAfterEach()
})
</script>

<style scoped>
.nav-transition {
  transition: transform 260ms cubic-bezier(0.22, 1, 0.36, 1), opacity 220ms ease;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes bounceSlow {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-2px); }
}

.animate-bounce-slow {
  animation: bounceSlow 1.2s infinite;
}

.nav-transition a.router-link-active {
  color: var(--accent, #ec2263);
}

.pill-badge {
  padding: 2px 8px;
  border-radius: 12px;
  background: #1f2d3d;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
}

.mobile-icon-btn {
  display: inline-flex;
  height: 42px;
  min-width: 42px;
  align-items: center;
  justify-content: center;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #fff;
  color: #1f2937;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
}

.mobile-profile-btn {
  gap: 4px;
  padding: 0 8px 0 5px;
}

@media (max-width: 767px) {
  .nav-transition {
    padding-left: 0;
    padding-right: 0;
  }
}
</style>
